<script setup>
  import { ref, onMounted } from 'vue'
  import { useAuthStore } from '@/stores/auth.store'
  import { useI18n } from 'vue-i18n'
  import savedChat from '@/assets/images/savedchat.webp'
  import { useRouter } from 'vue-router'
  import ChatCategoryPanel from '@/components/ChatCategoryPanel.vue'
  import MiniChat from '@/components/MiniChat.vue'
  import LanguageStreakHeader from '@/components/LanguageStreakHeader.vue'
  import { IonModal, IonHeader, IonToolbar, IonButtons, IonButton, IonTitle, IonContent } from '@ionic/vue'
  import { useCacheStore } from '@/stores/cache.store'
  import { analytics } from '@/helpers/analytics.js'

  const router = useRouter()
  const { t } = useI18n()
  const authStore = ref(false)
  const isLoading = ref(true)
  authStore.value = useAuthStore()
  const categories = ref([])
  const showMiniChat = ref(false)
  const selectedCategory = ref(false)
  const cacheStore = useCacheStore()

  onMounted(async () => {
    // Track page view
    await analytics.trackContentView('chat_categories', 'navigation', authStore.value.learningLanguage || 'unknown')
    
    await cacheStore.process('chat-category-list', '/chat/category/list/', async (data) => {
      categories.value = data
      isLoading.value = false
    })
  })

  function return_href(category) {
    console.log('get return_href for ', category)
    return {
      name: 'ChatTemplates',
      params: { categoryID: category.id }
    }
  }

  function go_to_category(category) {
    // Track category selection
    analytics.trackFeatureUsage('chat_category_selected', {
      category_id: category.id,
      category_name: category.name,
      language: authStore.value.learningLanguage || 'unknown'
    })
    
    if (category.name === 'tutor') {
      // Track tutor chat start
      analytics.trackChatStart('tutor', authStore.value.learningLanguage || 'unknown')
      
      // Open MiniChat directly for the "tutor" category
      selectedCategory.value = category
      showMiniChat.value = true
    } else {
      console.log("go_to_category", category)
      router.push(return_href(category))
    }
  }

  function to_to_history() {
    console.log("go_to_history")
    router.push("/chat/history/")
  }

  function closeMiniChat() {
    showMiniChat.value = false
    selectedCategory.value = false
  }
</script>

<template>
  <nice-header />
  <loading-overlay v-model:active="isLoading" :can-cancel="true" is_full_page="false" loader="dots" :opacity="0.8" />
  <section class="my-0">
    <div class="container">
      <language-streak-header />
      <div class="row">
        <div class="col-12">
          <div class="bg-white border-0 rounded border-light p-1 p-sm-2 p-md-5 w-100 fmxw-500">
            
            <div class="text-center text-md-center mb-4 mt-md-0 col-12">
              <h1 class="mb-0 h1-title">{{ t('categories.chat-topics') }}</h1>
              <hr />
              {{ t('categories.choose-from-one-of-these-fun-and-exciting') }}
            </div>
            <div class="d-flex justify-content-center gap-2"></div>
            <div class="row align-items-stretch">
              <div v-for="category in categories" :key="category.id" class="col-md-12 col-xl-6 mb-4"> 
                <ChatCategoryPanel @go_to="go_to_category(category)">
                  <template #image>
                    <div class="cardImageWrapper h-100">
                      <CCardImage :src="category.thumbnail_url" class="img-fluid thumbnail rounded-3" alt="image" />
                    </div>
                  </template>
                  <template #title>
                    <h2 class="mb-2 card-title text-start">{{ t('categories.' + category.name) }}</h2>
                  </template>
                  <template #details>
                    <p v-html="t(`chat-category.description.${category.id}`)"/>
                  </template>
                </ChatCategoryPanel>
              </div>
              <div class="col-md-12 col-xl-6 mb-4">
                <ChatCategoryPanel @go_to="to_to_history">
                  <template #image>
                    <div class="cardImageWrapper h-100">
                      <CCardImage width="125px" height="125px" :src="savedChat" class="img-fluid thumbnail rounded-3" alt="image" />
                    </div>
                  </template>
                  <template #title>
                    <h2 class="mb-2 card-title text-start">{{ t('navbar.active-chats') }}</h2>
                  </template>
                  <template #details>
                    <p v-html="t('content-block.text.1')"/>
                  </template>
                </ChatCategoryPanel>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- MiniChat Modal -->
  <ion-modal :is-open="showMiniChat" @didDismiss="closeMiniChat">
    <ion-header>
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-button @click="closeMiniChat()"><i class="bi bi-x action-icon"></i></ion-button>
        </ion-buttons>
        <ion-title><i class="bi bi-chat-dots-fill"></i> {{ t(`categories.${selectedCategory.name}`) }}</ion-title>
      </ion-toolbar>
    </ion-header>
    <ion-content class="ion-padding">
      <MiniChat v-if="showMiniChat" :templateID="230" @close="closeMiniChat" />
    </ion-content>
  </ion-modal>

  <nice-footer />
</template>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
h1,
h2 {
  font-weight: normal;
}
ul {
  list-style-type: none;
  padding: 0;
}
li {
  display: inline-block;
  margin: 0 10px;
}
.link-like {
  cursor: pointer;
}
.cardImageWrapper{
  position: relative;
}
.cardImageWrapper::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1;
    transition: .5s ease;
    border-radius:1rem;
}
.card-cs:hover .cardImageWrapper::before {
    background: rgba(0, 0, 0, 0);
    transition: .5s ease;
}
</style>

